import { download, generateCsv, mkConfig } from "export-to-csv"
import { DateTime } from "luxon"
import { useCallback } from "react"

import { ResourceType } from "../../../admin/organization/resources/resourcesTypes"
import { useDateTime } from "../../../../localization/useDateTime"
import { UsagePerTenantPerResourceEntry } from "../statisticsTypes"

const RESOURCE_TYPE_LABEL: Record<ResourceType, string> = {
    [ResourceType.ROOM]: "Meeting rooms",
    [ResourceType.MOBILE_AV]: "AV equipment",
    [ResourceType.CAR]: "Cars",
}

type UseExportUsagePerTenantParams = Readonly<{
    data: ReadonlyArray<UsagePerTenantPerResourceEntry> | null | undefined
    startDate: DateTime
    endDate: DateTime
}>

export function useExportUsagePerTenant({ data, startDate, endDate }: UseExportUsagePerTenantParams) {
    const dateTimeFactory = useDateTime()

    const csvOptions = mkConfig({
        fieldSeparator: ";",
        quoteStrings: true,
        decimalSeparator: ".",
        useBom: true,
        useKeysAsHeaders: true,
        filename: `Facilitynet-usage-per-tenant-${dateTimeFactory.formatDate(startDate)}_${dateTimeFactory.formatDate(
            endDate,
        )}`,
    })

    const mapEntryToCsvRow = (entry: UsagePerTenantPerResourceEntry) => ({
        From: dateTimeFactory.formatDate(startDate),
        To: dateTimeFactory.formatDate(endDate),
        "Resource type": entry.resource.resourceType ? RESOURCE_TYPE_LABEL[entry.resource.resourceType] : "",
        Resource: entry.resource.displayName,
        Tenant: entry.tenant.name,
        "Usage rate": entry.usageRate,
    })

    return useCallback(() => {
        const rows =
            data && data.length > 0
                ? data.map(mapEntryToCsvRow)
                : [mapEntryToCsvRow({ resource: { id: 0, displayName: "" }, tenant: { name: "" }, usageRate: 0 })]

        const csv = generateCsv(csvOptions)(rows)
        download(csvOptions)(csv)
    }, [dateTimeFactory, data, startDate, endDate])
}
